import { say } from '@/james-playground/aa-family-claim/claim/asks';
import { entry } from '@/james-playground/aa-family-claim/claim/registry';
import {
  CARD,
  VARIABILITY,
} from '@/james-playground/aa-family-claim/claim/cards/cards.content';
import {
  CardShell,
  ChoiceCards,
  Part,
  SuggestField,
  useDraft,
  useQids,
} from '@/james-playground/aa-family-claim/claim/cards/parts';
import { type CardProps } from '@/james-playground/aa-family-claim/claim/cards/types';
import { str } from '@/james-playground/aa-family-claim/claim/cards/values';

/**
 * Good and bad days (spec 10): about the same, some days or most days, and
 * when it is worse, how often. The answer feeds the sentence the claim drafts
 * for that area and for Q46.
 */
const VariabilityCard = ({
  ask,
  facts,
  voice: v,
  onSubmit,
  onSkip,
}: CardProps) => {
  const { draft, set, visible, values } = useDraft(ask, facts);
  const qid = useQids();
  const [choiceId, oftenId] = ask.entries;
  const kind = ask.area === 'night' ? 'night' : 'day';
  const choice = str(draft[choiceId]);
  const showOften = Boolean(oftenId) && visible(oftenId);
  const shown = VARIABILITY.options[kind];

  const ready =
    Boolean(choice) && (!showOften || str(draft[oftenId]).trim() !== '');

  return (
    <CardShell
      label={say(ask.title, v)}
      ready={ready}
      onSkip={onSkip}
      onContinue={() => {
        const out = values();
        const often = showOften ? str(out[oftenId]) : '';
        onSubmit(
          out,
          [shown[choice] ?? choice, often].filter(Boolean).join(', '),
        );
      }}
    >
      <ChoiceCards
        label={say(ask.prompt, v)}
        options={(entry(choiceId).options ?? []).map((o) => ({
          value: o.label,
          label: shown[o.label] ?? o.label,
        }))}
        selected={choice ? [choice] : []}
        onPick={(value) => set(choiceId, value)}
      />
      {showOften && (
        <Part qid={qid('often')} question={VARIABILITY.often[kind]}>
          <SuggestField
            labelledBy={qid('often')}
            chips={VARIABILITY.chips[kind]}
            other={CARD.somethingElse}
            field={VARIABILITY.field}
            value={str(draft[oftenId])}
            onChange={(value) => set(oftenId, value)}
          />
        </Part>
      )}
    </CardShell>
  );
};

VariabilityCard.displayName = 'VariabilityCard';
export default VariabilityCard;
